/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * @file useModuleNav.js
 * @description
 * Turns a module group id + the currently-active tab into ready-to-render
 * {@link module:components/module/ModuleTabBar} props. This is the glue between
 * the static {@link module:config/moduleNavs} taxonomy and React Navigation:
 * it wires each tab's `onPress` to the right action (open a list / swap to a
 * screen / switch an in-screen view) and provides the "back to main nav" exit.
 *
 * Sibling module screens are swapped with {@link StackActions.replace} (not
 * pushed) so hopping between a module's tabs keeps the stack flat — tab
 * semantics, not a growing back-stack. The exit returns to the mode's main tab
 * navigator via `navigate('ManagementHomeScreen')` (or a caller-supplied
 * `onExit`, e.g. Mail switching back to a sibling tab).
 */

import {useMemo} from 'react';
import {StackActions, useNavigation} from '@react-navigation/native';
import {useTranslation} from 'react-i18next';
import {getModuleGroup} from '../config/moduleNavs';
import {useServiceMode} from './ServiceModeContext';

/**
 * Maps a `service` module-group tab key to the service-mode feature key that
 * gates it — additive, fail-open filter applied on top of the static
 * {@link module:config/moduleNavs} taxonomy (that file itself stays untouched).
 * @type {Object.<string, string>}
 */
const SERVICE_TAB_FEATURE = {
    appointments: 'bookings',
};

/**
 * @typedef {object} ModuleNavProps
 * @property {Array<{key:string,label:string,icon:string,active:boolean,badge?:(number|string),onPress:()=>void}>} tabs
 * @property {()=>void} onExit     Return to the main app navigation.
 * @property {string} menuLabel    Localized label for the exit pill.
 */

/**
 * Build the module bottom-bar model for a group.
 *
 * @param {object} params
 * @param {string} [params.group]        Module group id (see MODULE_GROUPS). Falsy → returns null.
 * @param {string} [params.activeTab]    Key of the currently-active tab (for highlight + no-op guard).
 * @param {(view:string)=>void} [params.onView]  Handler for `view`-action tabs (in-screen switch).
 * @param {()=>void} [params.onExit]     Override the default exit-to-main-nav behaviour.
 * @param {Object.<string, number|string>} [params.badges]  Per-tab badge values keyed by tab key.
 * @param {string} [params.exitRoute]    Route to return to on exit (default 'ManagementHomeScreen').
 * @returns {ModuleNavProps|null}
 */
export const useModuleNav = ({group, activeTab, onView, onExit, badges, exitRoute = 'ManagementHomeScreen'} = {}) => {
    const navigation = useNavigation();
    const {t} = useTranslation();
    const {isFeatureEnabled} = useServiceMode();

    return useMemo(() => {
        const def = getModuleGroup(group);
        if (!def) {
            return null;
        }

        const visibleTabs = group === 'service'
            ? def.tabs.filter((tab) => {
                const featureKey = SERVICE_TAB_FEATURE[tab.key];
                return !featureKey || isFeatureEnabled(featureKey);
            })
            : def.tabs;

        const tabs = visibleTabs.map((tab) => ({
            key: tab.key,
            label: t(tab.labelKey) || tab.fallback,
            icon: tab.icon,
            active: tab.key === activeTab,
            badge: badges ? badges[tab.key] : undefined,
            onPress: () => {
                if (tab.key === activeTab) {
                    return;
                }
                if (tab.view) {
                    onView?.(tab.view);

                    return;
                }
                if (tab.list) {
                    navigation.dispatch(StackActions.replace('ManagementListScreen', {
                        type: tab.list,
                        moduleGroup: group,
                        moduleTab: tab.key,
                    }));

                    return;
                }
                if (tab.screen) {
                    navigation.dispatch(StackActions.replace(tab.screen, {moduleGroup: group, moduleTab: tab.key}));
                }
            },
        }));

        const exit = onExit || (() => navigation.navigate(exitRoute));

        return {tabs, onExit: exit, menuLabel: t('back') || 'Vissza'};
    }, [group, activeTab, onView, onExit, badges, exitRoute, navigation, t, isFeatureEnabled]);
};

export default useModuleNav;
